import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

const toneColor = (theme, tone) => {
  if (tone === "success") return theme.palette.secondary.main;
  if (tone === "warning") return theme.palette.warning.main;
  if (tone === "error") return theme.palette.error.main;
  return theme.palette.text.primary;
};

/**
 * Shared visual frame for certification experiences. Role screens supply their
 * own data and actions, while this component keeps the mock's hierarchy,
 * responsive summary strip, and tab treatment consistent.
 */
export default function CertificationShell({
  eyebrow,
  title,
  description,
  action,
  summary,
  stats = [],
  tabs = [],
  activeTab,
  onTabChange,
  children,
}) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const ink = theme.palette.text.primary;
  const muted = theme.palette.text.secondary;
  const hairline = alpha(ink, 0.14);
  const displayFont = "'Space Grotesk', 'Rubik', " + theme.typography.fontFamily;
  const bodyFont = "'Rubik', " + theme.typography.fontFamily;

  return (
    <Box
      component="section"
      data-testid="certification-shell"
      sx={{ maxWidth: 1240, mx: "auto", width: "100%", fontFamily: bodyFont }}
    >
      {(title || eyebrow || description || action) && (
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2.5, flexWrap: "wrap", mb: 3 }}>
          <Box>
            {eyebrow && (
              <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: ".02em", textTransform: "uppercase", color: primary, mb: 0.75 }}>
                {eyebrow}
              </Typography>
            )}
            {title && (
              <Typography component="h1" sx={{ fontFamily: displayFont, fontSize: { xs: 28, sm: 32 }, fontWeight: 600, letterSpacing: "-.01em", color: ink, lineHeight: 1.15 }}>
                {title}
              </Typography>
            )}
            {description && (
              <Typography sx={{ fontSize: 14.5, color: muted, mt: 0.75, maxWidth: 560, lineHeight: 1.5 }}>
                {description}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
      )}

      {summary}

      {stats.length > 0 && (
        <Box
          aria-label="Certification summary"
          sx={{ display: "flex", flexWrap: "wrap", overflow: "hidden", bgcolor: "background.paper", border: `1px solid ${hairline}`, borderRadius: 1.5, mb: 3 }}
        >
          {stats.map((stat, index) => {
            const color = toneColor(theme, stat.tone);
            return (
              <Box key={stat.label} sx={{ flex: "1 1 180px", minWidth: 0, px: { xs: 2, sm: 2.75 }, py: 2.25, borderLeft: { xs: index % 2 ? `1px solid ${hairline}` : 0, sm: index ? `1px solid ${hairline}` : 0 }, borderTop: { xs: index > 1 ? `1px solid ${hairline}` : 0, sm: 0 } }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, letterSpacing: ".02em", textTransform: "uppercase", color: muted, mb: 1 }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ color, fontFamily: displayFont, fontSize: 26, fontWeight: 600, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                  {stat.value}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {tabs.length > 0 && (
        <Box role="tablist" aria-label="Certification views" sx={{ display: "flex", gap: 0.5, borderBottom: `1px solid ${hairline}`, mb: 2.5, flexWrap: "wrap" }}>
          {tabs.map((tab) => {
            const selected = tab.id === activeTab;
            return (
              <Button
                key={tab.id}
                role="tab"
                data-testid={tab.testId}
                aria-selected={selected}
                onClick={() => onTabChange?.(tab.id)}
                disableRipple
                sx={{
                  minWidth: 0, flexShrink: 0, color: selected ? primary : muted, opacity: selected ? 1 : 0.75,
                  borderBottom: `2px solid ${selected ? primary : "transparent"}`, borderRadius: 0, mb: "-1px", px: 0.5, mr: 2.5, py: 1.25,
                  fontFamily: bodyFont, fontSize: 14.5, fontWeight: selected ? 600 : 500, textTransform: "none",
                  "&:hover": { bgcolor: "transparent", color: selected ? primary : ink, opacity: 1 },
                }}
              >
                {tab.label}
                {tab.count != null && (
                  <Box component="span" sx={{ ml: 1, px: 0.875, py: "1px", borderRadius: 99, bgcolor: selected ? alpha(primary, 0.1) : alpha(ink, 0.06), color: "inherit", fontSize: 11, fontWeight: 700, lineHeight: 1.45, fontVariantNumeric: "tabular-nums" }}>
                    {tab.count}
                  </Box>
                )}
              </Button>
            );
          })}
        </Box>
      )}

      {children}
    </Box>
  );
}
